import { TableHead, TableRow, TableSortLabel } from "@mui/material"
import { Order, SortOrder } from "../../models/SortOrder"
import { HandleSelect, NexHeaderCellPropsBase, TableColumn } from "../../models/Table"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { NexCheckbox } from "../Fields/Checkbox"
import { NexTableCell } from "./NexTableCell"

interface TableHeaderProps<T> {
    tableLabel: string
    columns: TableColumn<T>[]
    handleSelect?: HandleSelect
    numSelected?: number
    numSelectable?: number
    stickyColumns?: TableColumn<T>[]
    canSelect?: boolean
    disableSelectAll?: boolean
    rowOverlay?: boolean
}

export const NexTableHeader = <T,>({
    tableLabel,
    columns,
    stickyColumns,
    canSelect,
    disableSelectAll,
    handleSelect,
    numSelected,
    numSelectable,
    rowOverlay,
}: TableHeaderProps<T>) => {
    const sortOrderMap = useLocalStorageStore((state) => state.tableSortOrders)
    const setTableSortOrder = useLocalStorageStore((state) => state.setTableSortOrder)
    const sortOrder = (sortOrderMap[tableLabel] ?? { orderBy: "", order: Order.asc }) as SortOrder<keyof T>

    const setSortOrder = (orderBy: keyof T, order: Order) => {
        setTableSortOrder(tableLabel, orderBy, order)
    }

    const checkbox = disableSelectAll ? null : (
        <NexTableCell>
            <NexCheckbox
                indeterminate={!!numSelected && numSelected !== numSelectable}
                checked={canSelect && numSelected === numSelectable}
                onClick={handleSelect && (() => handleSelect(false))}
            />
        </NexTableCell>
    )

    return (
        <TableHead>
            <TableRow>
                {stickyColumns && (
                    <NexTableCell stickyHeader={true}>
                        {checkbox}
                        {stickyColumns.map((column, index) => (
                            <NexTableCell key={index} column={column}>
                                <NexHeaderCell
                                    sortOrder={sortOrder}
                                    setSortOrder={setSortOrder}
                                    label={column.label}
                                    columnId={column.id}
                                    convertType={column.convertType}
                                />
                            </NexTableCell>
                        ))}
                    </NexTableCell>
                )}
                {!stickyColumns && <>{checkbox}</>}
                {columns.map((column, index) => (
                    <NexTableCell key={index} column={column}>
                        <NexHeaderCell
                            sortOrder={sortOrder}
                            setSortOrder={setSortOrder}
                            label={column.label}
                            columnId={column.id}
                            convertType={column.convertType}
                        />
                    </NexTableCell>
                ))}
                {/* Makes header style have an empty column so it doesn't look empty */}
                {rowOverlay && <NexTableCell />}
            </TableRow>
        </TableHead>
    )
}

interface NexHeaderCellProps<T> extends NexHeaderCellPropsBase {
    columnId?: keyof T
    sortOrder: SortOrder<keyof T>
    setSortOrder: (orderBy: keyof T, order: Order) => void
}

export const NexHeaderCell = <T,>(props: NexHeaderCellProps<T>) => {
    const { label, columnId, sortOrder, setSortOrder } = props
    const { order = Order.asc, orderBy } = sortOrder

    const createSortHandler = () => {
        if (columnId) {
            const isAsc = orderBy === columnId && order === Order.asc
            setSortOrder(columnId, isAsc ? Order.desc : Order.asc)
        }
    }

    return (
        <TableSortLabel
            disabled={!columnId}
            active={orderBy === columnId}
            direction={orderBy === columnId ? order : Order.asc}
            onClick={createSortHandler}
        >
            {label}
        </TableSortLabel>
    )
}
